import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { toast } from "sonner";
import { SectionCard, ReasonDialog } from "./shared";
import { accountingSetupBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

export default function AccountingSettingsPage() {
  const [setup, setSetup] = useState(accountingSetupBlank);
  const [loading, setLoading] = useState(true);
  const [periodLock, setPeriodLock] = useState(null);
  const [newLockDate, setNewLockDate] = useState("");
  const [setLockOpen, setSetLockOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [documentProfile, setDocumentProfile] = useState({ societyName: "", address: "", registrationNumber: "" });
  const [profileBusy, setProfileBusy] = useState(false);

  const loadPeriodLock = async () => {
    try {
      const res = await api.get("/society/accounting/administration/period-lock");
      setPeriodLock(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load the Accounting Period Lock"));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [res, profileRes] = await Promise.all([
          api.get("/society/accounting/setup").catch(() => ({ data: accountingSetupBlank })),
          api.get("/society/accounting/document-profile").catch(() => ({ data: {} })),
        ]);
        setSetup({ ...accountingSetupBlank, ...(res.data || {}) });
        setDocumentProfile({ societyName: "", address: "", registrationNumber: "", ...(profileRes.data || {}) });
      } catch (err) {
        toast.error(formatErr(err, "Could not load accounting settings"));
      } finally {
        setLoading(false);
      }
    })();
    loadPeriodLock();
  }, []);

  const saveDocumentProfile = async () => {
    if (!documentProfile.societyName.trim()) { toast.error("Society Name is required"); return; }
    try {
      setProfileBusy(true);
      const res = await api.put("/society/accounting/document-profile", documentProfile);
      setDocumentProfile(res.data);
      toast.success("Voucher print header updated");
    } catch (err) {
      toast.error(formatErr(err, "Could not update voucher print header"));
    } finally {
      setProfileBusy(false);
    }
  };

  const setLock = async (reason) => {
    try {
      setBusy(true);
      await api.put("/society/accounting/administration/period-lock", { lockedThroughDate: newLockDate, reason });
      toast.success(`Locked through ${newLockDate}`);
      setSetLockOpen(false); setNewLockDate("");
      await loadPeriodLock();
    } catch (err) {
      toast.error(formatErr(err, "Could not set the Accounting Period Lock"));
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (reason) => {
    try {
      setBusy(true);
      await api.post("/society/accounting/administration/period-lock/unlock", { reason });
      toast.success("Accounting Period Lock removed");
      setUnlockOpen(false);
      await loadPeriodLock();
    } catch (err) {
      toast.error(formatErr(err, "Could not remove the Accounting Period Lock"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Accounting Settings</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Current go-live configuration for this Society's accounting module.</p>
      </div>

      <SectionCard title="Go-live status">
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <div><div className="text-xs text-[#6B7280]">Financial year</div><div className="font-semibold text-[#111827]">{setup.financialYear || "Not set"}</div></div>
            <div><div className="text-xs text-[#6B7280]">Go-live date</div><div className="font-semibold text-[#111827]">{setup.goLiveDate || "-"}</div></div>
            <div><div className="text-xs text-[#6B7280]">Migration mode</div><div className="font-semibold capitalize text-[#111827]">{(setup.migrationMode || "-").replace(/_/g, " ")}</div></div>
            <div><div className="text-xs text-[#6B7280]">Status</div><div className="font-semibold capitalize text-[#111827]">{setup.status || "draft"}</div></div>
          </div>
        )}
        <p className="mt-3 text-xs text-[#6B7280]">
          Edit these from <Link to={`${ACCOUNTING_BASE}/financial-years`} className="underline">Financial Years</Link> or <Link to={`${ACCOUNTING_BASE}/migration`} className="underline">Migration / Go-Live</Link>.
        </p>
      </SectionCard>

      <SectionCard
        title="Society Document Header"
        subtitle="These details appear on printed accounting vouchers. They do not change login or member information."
        actions={<Button size="sm" disabled={profileBusy || !documentProfile.societyName.trim()} onClick={saveDocumentProfile}>Save Header</Button>}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Society Name</div>
            <Input value={documentProfile.societyName} onChange={(e) => setDocumentProfile((p) => ({ ...p, societyName: e.target.value }))} placeholder="Registered society name" />
          </div>
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Society Registration No.</div>
            <Input value={documentProfile.registrationNumber} onChange={(e) => setDocumentProfile((p) => ({ ...p, registrationNumber: e.target.value }))} placeholder="Registration number" />
          </div>
          <div className="md:col-span-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Registered Address</div>
            <Input value={documentProfile.address} onChange={(e) => setDocumentProfile((p) => ({ ...p, address: e.target.value }))} placeholder="Complete society address" />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Accounting Period Lock"
        subtitle="Blocks creating, posting, editing or cancelling any voucher dated on or before the Locked Through date, for normal users."
        actions={
          <div className="flex gap-2">
            {periodLock?.lockedThroughDate && <Button variant="outline" size="sm" onClick={() => setUnlockOpen(true)}>Remove Lock</Button>}
            <Button size="sm" onClick={() => setSetLockOpen(true)}>{periodLock?.lockedThroughDate ? "Change Locked Through Date" : "Set Locked Through Date"}</Button>
          </div>
        }
      >
        {!periodLock ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : periodLock.lockedThroughDate ? (
          <div className="grid gap-3 sm:grid-cols-3 text-sm">
            <div><div className="text-xs text-[#6B7280]">Locked Through</div><div className="font-semibold text-[#111827]">{periodLock.lockedThroughDate}</div></div>
            <div><div className="text-xs text-[#6B7280]">Last changed by</div><div className="font-semibold text-[#111827]">{periodLock.lastChangedBy || "-"}</div></div>
            <div><div className="text-xs text-[#6B7280]">Last changed at</div><div className="font-semibold text-[#111827]">{periodLock.lastChangedAt ? new Date(periodLock.lastChangedAt).toLocaleString() : "-"}</div></div>
            {periodLock.reason && <div className="sm:col-span-3"><div className="text-xs text-[#6B7280]">Reason</div><div className="text-[#374151]">{periodLock.reason}</div></div>}
          </div>
        ) : (
          <div className="text-sm text-[#6B7280]">No Accounting Period Lock is set. All dates are open for posting (subject to each Financial Year's own status).</div>
        )}
      </SectionCard>

      {setLockOpen && (
        <SectionCard title="Choose the new Locked Through date">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-48 space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Locked Through Date</div>
              <Input type="date" value={newLockDate} onChange={(e) => setNewLockDate(e.target.value)} />
            </div>
            <Button variant="outline" onClick={() => setSetLockOpen(false)}>Cancel</Button>
          </div>
        </SectionCard>
      )}

      <SectionCard title="Coming in later phases">
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-[#6B7280]">
          <li>Fine-grained accounting permission roles</li>
          <li>Full automated year-end closing entries (Financial Year status today is informational only)</li>
        </ul>
      </SectionCard>

      <ReasonDialog
        open={setLockOpen && !!newLockDate} onOpenChange={(v) => !v && setSetLockOpen(false)} busy={busy}
        title="Confirm Accounting Period Lock change" description={`This will block posting/editing/cancelling any voucher dated on or before ${newLockDate}. A reason is required and every change is audited.`}
        confirmLabel="Confirm Lock" onConfirm={setLock}
      />
      <ReasonDialog
        open={unlockOpen} onOpenChange={setUnlockOpen} busy={busy}
        title="Remove the Accounting Period Lock?" description="This fully reopens the locked period. A reason is required and this action is audited."
        confirmLabel="Remove Lock" onConfirm={unlock}
      />
    </div>
  );
}
